import React, { useRef } from "react";
import {
    Alert, AlertProps, AlertTitle, List,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import ReactDOM from "react-dom";
import { RootState } from "../../store";
import { InternalAlert, removeAlert } from "./alertsSlice";
import {
    SPACING_4, SPACING_8, SPACING_300, SPACING_16,
} from "../common/styles/spacing";
import { Z_INDEX_ALERT } from "../common/styles/z-index";

interface StyledAlertProps extends AlertProps {
    ttl: number;
}

// Instead of using js to handle the showing and hiding of the alert, we extend css to handle it
const SlideIn = keyframes`
    0% {
        opacity: 0;
        transform: translateX(100%);
    }

    50% {
        opacity: 1;
    }

    100% {
        transform: translateX(0%);
    }
`;

const StyledAlert = styled(Alert)`
    display: flex;
    align-items: start;
    word-break: break-word;

    margin-top: ${SPACING_4};
    animation: ${SlideIn} 800ms ease-in-out,
               ${SlideIn} 800ms ease-in-out ${(props: StyledAlertProps) => props.ttl}ms reverse;
    animation-fill-mode: forwards;

    width: ${SPACING_300};
`;

const StyledList = styled(List)`
    height: 100%;

    overflow-x: hidden;
    margin-right: ${SPACING_8};
`;

const StyledAlertContainer = styled.div`
    display: flex;
    position: absolute;

    z-index: ${Z_INDEX_ALERT};

    font-family: Questrial;

    top: ${SPACING_16};

    max-width: ${SPACING_300};
    max-height: 40%;
    right: 0;
`;

const StyledAlertWrapper = styled.div`
    position: relative;
    max-height: 40%;
`;

export default () => {
    const alerts = useSelector<RootState>((state) => state.alerts.alerts);
    const alertsSeenRef = useRef<{ [keys: number]: boolean }>({});
    const dispatch = useDispatch();

    const handleRemoveAlert = (date: number) => dispatch(removeAlert(date));

    // Due to the fact we have 2 animations,
    // one to handle showing the alert, and one to handle hiding the alert
    // We have to keep track of the animation state of the alert to know when to remove it,
    // We do that in a ref to avoid additional rerenders
    const handleAnimationEnd = (date: number) => {
        if (alertsSeenRef.current[date]) {
            handleRemoveAlert(date);
        } else {
            alertsSeenRef.current[date] = true;
        }
    };

    return ReactDOM.createPortal(
        <StyledAlertContainer>
            <StyledAlertWrapper>
                <StyledList>
                    {(alerts as InternalAlert[]).map(({
                        title, text, type, variant, date, ttl,
                    }) => (
                        <StyledAlert
                            key={date}
                            onClose={() => handleRemoveAlert(date)}
                            onAnimationEnd={() => handleAnimationEnd(date)}
                            ttl={ttl}
                            variant={variant}
                            severity={type}
                        >
                            {title && <AlertTitle>{title}</AlertTitle>}
                            {text}
                        </StyledAlert>
                    ))}
                </StyledList>
            </StyledAlertWrapper>
        </StyledAlertContainer>,
        document.body,
    );
};
